// src/services/deviceMetaService.js
// Fetches and updates device metadata (name + location overrides) from the
// auth backend. Applies overrides into the TTN device mapper at runtime.

import { AUTH } from './config';
import { applyMetaOverrides } from './ttn/devices';
import DEVICE_NAMES from './ttn/deviceNames';
import DEVICE_LOCATIONS from './ttn/deviceLocations';

const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('authToken') || ''}`,
});

/**
 * Load all device metadata from the backend and apply overrides.
 * Falls back silently if the backend is unavailable — static defaults remain.
 */
export const loadDeviceMeta = async () => {
  try {
    const res = await fetch(`${AUTH.BASE_URL}/device-meta`);
    if (!res.ok) return;
    const json = await res.json();
    if (json.success && json.data) {
      applyMetaOverrides(json.data);
    }
  } catch {
    // Backend unavailable — static deviceNames/deviceLocations used as fallback
  }
};

/**
 * Save a device's name and/or location.
 * @param {string} deviceId
 * @param {{ name?: string, location?: string }} updates
 */
export const saveDeviceMeta = async (deviceId, updates) => {
  const res = await fetch(`${AUTH.BASE_URL}/device-meta/${deviceId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.message || `HTTP ${res.status}`);
  // Immediately update the runtime override so the UI reflects the change
  applyMetaOverrides({ ...getCurrentMeta(), [deviceId]: updates });
  return json.data;
};

/**
 * Delete stored overrides for a device (reverts to static defaults).
 */
export const resetDeviceMeta = async (deviceId) => {
  const res = await fetch(`${AUTH.BASE_URL}/device-meta/${deviceId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.message || `HTTP ${res.status}`);
  }
};

/**
 * Returns a merged list of all known devices with their current name/location,
 * combining static defaults with any backend overrides.
 * Used to populate the admin Device Management table.
 */
export const getAllDevicesMeta = async () => {
  // Fetch latest from backend
  let dbMeta = {};
  try {
    const res = await fetch(`${AUTH.BASE_URL}/device-meta`);
    if (res.ok) {
      const json = await res.json();
      if (json.success) dbMeta = json.data || {};
    }
  } catch { /* use empty */ }

  // Build merged list from static keys
  const allIds = new Set([
    ...Object.keys(DEVICE_NAMES),
    ...Object.keys(dbMeta),
  ]);

  return Array.from(allIds).map((id) => ({
    deviceId: id,
    name:     dbMeta[id]?.name     || DEVICE_NAMES[id]     || '',
    location: dbMeta[id]?.location || DEVICE_LOCATIONS[id] || '',
    hasOverride: !!dbMeta[id],
  }));
};

// Internal helper to get current runtime overrides map
const getCurrentMeta = () => {
  // We re-fetch lazily; this is only used for the immediate optimistic update
  return {};
};
